import { useState } from 'react';
import { Alert, DatePicker, Flex, Form, Modal, Radio, Select, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { billingIntervalAsSuffix, EBillingInterval } from '@leedpulse-monorepo/enums';
import { IPlan, IScheduledPlanChange } from '@leedpulse-monorepo/interfaces';
import { cancellationDateFor, isPlanUpgrade } from '@leedpulse-monorepo/utils';

interface ChangePlanModalProps {
  currentPlan: IPlan;
  plans: IPlan[]; // Every plan, the targets are the ones the current plan leads to
  billingPeriodEnd: number | null; // Company Stripe period end: when a downgrade switches the plan
  scheduledPlanChange: IScheduledPlanChange | null; // Replaced by a new request
  isBusy: boolean;
  onClose: () => void;
  onSubmit: (newPlanId: string, scheduledDate: string | null) => Promise<boolean>;
}

const formatDay = (ms: number) => dayjs(ms).format('DD/MM/YYYY');

/** The plans the current one leads to. Archiving a plan does not remove it from the lists, as in the client portal */
export const changePlanTargetIds = (currentPlan: IPlan, plans: IPlan[]) =>
  [...new Set([...(currentPlan.upgradable_to ?? []), ...(currentPlan.downgradable_to ?? [])])]
    .filter((id) => !plans.find((p) => p.id === id)?.archived);

const ChangePlanModal: React.FC<ChangePlanModalProps> = ({
  currentPlan,
  plans,
  billingPeriodEnd,
  scheduledPlanChange,
  isBusy,
  onClose,
  onSubmit,
}) => {
  const [newPlanId, setNewPlanId] = useState<string | null>(null);
  const [isNow, setIsNow] = useState(true);
  const [date, setDate] = useState<Dayjs | null>(null);

  // Same rule as the backend: a plan in both lists is told apart by its price
  const targets = changePlanTargetIds(currentPlan, plans)
    .map((id) => {
      const plan = plans.find((p) => p.id === id);
      return { id, plan, isUpgrade: isPlanUpgrade(currentPlan, id, plan ?? {}) === true };
    })
    .sort((a, b) => Number(b.isUpgrade) - Number(a.isUpgrade));

  const target = targets.find((t) => t.id === newPlanId) ?? null;
  const canSubmit = target !== null && (isNow || date !== null) && !isBusy;

  // A downgrade switches the plan at the first billing date from the day chosen, as the client portal does
  const intervalMonths = currentPlan.price?.interval === EBillingInterval.YEAR ? 12 : 1;
  const downgradeDay = billingPeriodEnd
    ? formatDay(isNow ? billingPeriodEnd : cancellationDateFor(billingPeriodEnd, intervalMonths, date?.startOf('day').valueOf()))
    : null;
  const when = isNow ? 'tout de suite' : date ? `le ${date.format('DD/MM/YYYY')}` : 'le jour choisi';

  const summary = target === null ? null : target.isUpgrade
    ? `Upgrade ${when} : le prorata jusqu'à la prochaine facturation est facturé et prélevé sur Stripe, et les crédits du nouveau plan sont ajoutés au prorata du cycle en cours.`
    : `Downgrade : le prix Stripe passe au nouveau plan ${isNow ? 'tout de suite' : `d'ici le ${date ? date.format('DD/MM/YYYY') : 'jour choisi'}`}, sans prorata. ` +
      `Le plan, les crédits et les droits basculent à l'échéance ${downgradeDay ? `du ${downgradeDay}` : 'suivante'}.`;

  const submit = async () => {
    if (!canSubmit || newPlanId === null) return;
    const done = await onSubmit(newPlanId, isNow ? null : date?.format('YYYY-MM-DD') ?? null);
    if (done) onClose();
  };

  return (
    <Modal
      title="Changer de plan"
      centered
      open
      onCancel={onClose}
      closable={!isBusy}
      maskClosable={!isBusy}
      okText={isNow ? 'Changer maintenant' : 'Planifier'}
      cancelText="Annuler"
      confirmLoading={isBusy}
      okButtonProps={{ disabled: !canSubmit }}
      onOk={submit}
    >
      <Form layout="vertical" style={{ marginTop: '1rem' }}>
        <Form.Item label="Nouveau plan" required extra={`Plan actuel : ${currentPlan.name}. Seuls les plans vers lesquels il mène sont proposés.`}>
          <Select
            placeholder="Choisir un plan..."
            value={newPlanId}
            disabled={isBusy}
            onChange={(value) => setNewPlanId(value)}
            options={targets.map((t) => ({
              value: t.id,
              label: `${t.isUpgrade ? '↑' : '↓'} ${t.plan?.name ?? t.id}` +
                (t.plan ? ` · ${t.plan.price?.formatted ?? ''}${t.plan.price?.interval ? billingIntervalAsSuffix(t.plan.price.interval) : ''} · ${t.plan.credits?.leedbucks ?? 0} crédits` : ''),
            }))}
          />
        </Form.Item>
        <Form.Item label="Application" required>
          <Flex gap={12} align="center" wrap>
            <Radio.Group value={isNow} disabled={isBusy} onChange={(e) => setIsNow(e.target.value)}>
              <Radio value={true}>Maintenant</Radio>
              <Radio value={false}>À une date</Radio>
            </Radio.Group>
            {!isNow && (
              <DatePicker
                format="DD/MM/YYYY"
                value={date}
                disabled={isBusy}
                onChange={(value) => setDate(value)}
                disabledDate={(day) => day.isBefore(dayjs().add(1, 'day').startOf('day'))}
                placeholder="À partir de demain"
              />
            )}
          </Flex>
        </Form.Item>
        {summary && <Alert type="info" showIcon style={{ marginBottom: 16 }} message={summary} />}
        {scheduledPlanChange && (
          <Typography.Text type="warning">
            {`Le changement déjà planifié le ${formatDay(scheduledPlanChange.scheduled_at)} sera remplacé.`}
          </Typography.Text>
        )}
      </Form>
    </Modal>
  );
};

export default ChangePlanModal;